import { createClient } from '@supabase/supabase-js'

// Make sure we're using the new Supabase project URL from env variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Validate the Supabase URL
if (!supabaseUrl) {
  console.error('VITE_SUPABASE_URL is not defined. Authentication will not work properly.')
}

if (!supabaseAnonKey) {
  console.error('VITE_SUPABASE_ANON_KEY is not defined. Authentication will not work properly.')
}

// Create the Supabase client with improved options for reliability
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key)
        } catch (error) {
          console.error('Error getting auth item from localStorage', error)
          return null
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value)
        } catch (error) {
          console.error('Error setting auth item in localStorage', error)
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key)
        } catch (error) {
          console.error('Error removing auth item from localStorage', error)
        }
      },
    },
  },
  global: {
    headers: {
      'X-Client-Info': 'FlowDocs App'
    }
  }
})

// Log the Supabase project URL for debugging
console.log('Initialized Supabase client with URL:', supabaseUrl)

// Salesforce OAuth configuration
export const salesforceOAuthConfig = {
  // Production and sandbox login URLs
  login_url: import.meta.env.VITE_SALESFORCE_LOGIN_URL || 'https://login.salesforce.com',
  sandbox_login_url: import.meta.env.VITE_SALESFORCE_SANDBOX_LOGIN_URL || 'https://test.salesforce.com',

  // OAuth configuration
  clientId: import.meta.env.VITE_SALESFORCE_CONSUMER_KEY || '',
  clientSecret: import.meta.env.VITE_SALESFORCE_CONSUMER_SECRET || '',
  // NO FALLBACK - must be set in environment variables
  redirect_uri: import.meta.env.VITE_SALESFORCE_CALLBACK_URL,
  scope: ['api', 'refresh_token', 'openid'],
  
  // Store both production and sandbox URL structures for backward compatibility
  production: {
    authorizationEndpoint: 'https://login.salesforce.com/services/oauth2/authorize',
    tokenUrl: 'https://login.salesforce.com/services/oauth2/token',
    userInfoUrl: 'https://login.salesforce.com/services/oauth2/userinfo'
  },
  sandbox: {
    authorizationEndpoint: 'https://test.salesforce.com/services/oauth2/authorize',
    tokenUrl: 'https://test.salesforce.com/services/oauth2/token',
    userInfoUrl: 'https://test.salesforce.com/services/oauth2/userinfo'
  },
  
  // Helper function to get the correct endpoint based on environment
  getEndpoints: (environment: string = 'production') => {
    return environment === 'sandbox' ? 
      salesforceOAuthConfig.sandbox : 
      salesforceOAuthConfig.production;
  }
}

// Function to retrieve Salesforce data with the authenticated session
export const getSalesforceData = async (url: string, accessToken: string) => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      throw new Error(`Salesforce API error: ${response.status}`)
    }
    
    return await response.json()
  } catch (error) {
    console.error('Error fetching Salesforce data:', error)
    throw error
  }
}

// Function to handle Salesforce token refresh
export const refreshSalesforceToken = async (refreshToken: string) => {
  try {
    console.log('Refreshing Salesforce token using Edge Function');
    
    // Call the Supabase Edge Function that handles the token refresh
    const { data, error } = await supabase.functions.invoke('salesforce-auth', {
      body: {
        action: 'token',
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: salesforceOAuthConfig.clientId,
        client_secret: salesforceOAuthConfig.clientSecret
      }
    });
    
    if (error) {
      console.error('Error refreshing token via Edge Function:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error refreshing Salesforce token:', error)
    throw error
  }
}